import React, { useState, useMemo, useEffect } from 'react';
import {
  Package, Search, Calendar, X, Eye,
  Truck, CheckSquare, Square
} from 'lucide-react';
import { DELIVERY_STATUS } from '../constants/status';

// 状态徽章
const StatusBadge = ({ status, type }) => {
  const colorMap = {
    // 入库单 - 结算状态
    '未发起': 'bg-slate-100 text-slate-700',
    '已发起': 'bg-green-100 text-green-700',
    // 送货状态
    'stored': 'bg-purple-100 text-purple-700',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 送货单状态徽章
const DeliveryStatusBadge = ({ status }) => {
  const statusConfig = {
    [DELIVERY_STATUS.WAITING_DELIVERY]: { label: '待发货', color: 'bg-blue-100 text-blue-700' },
    [DELIVERY_STATUS.ARRIVED]: { label: '已到场', color: 'bg-indigo-100 text-indigo-700' },
    [DELIVERY_STATUS.WAITING_APPROVAL]: { label: '待审批', color: 'bg-red-100 text-red-700' },
    [DELIVERY_STATUS.STORED]: { label: '已入库', color: 'bg-purple-100 text-purple-700' },
    [DELIVERY_STATUS.PENDING_SETTLEMENT]: { label: '待结算', color: 'bg-orange-100 text-orange-700' },
    [DELIVERY_STATUS.SETTLED]: { label: '已结算', color: 'bg-cyan-100 text-cyan-700' },
    [DELIVERY_STATUS.INVOICED]: { label: '已开票', color: 'bg-teal-100 text-teal-700' },
    [DELIVERY_STATUS.PAID]: { label: '已付款', color: 'bg-green-100 text-green-700' },
  };

  const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// 批量结算弹窗组件
const BatchSettlementModal = ({ isOpen, onClose, onConfirm, stockins, onToggleSelect, selectedIds }) => {
  // 按供应商分组
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    stockins.forEach(doc => {
      if (!groups[doc.supplierName]) {
        groups[doc.supplierName] = [];
      }
      groups[doc.supplierName].push(doc);
    });
    return groups;
  }, [stockins]);

  const selectedDocs = stockins.filter(doc => selectedIds.includes(doc.id));
  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0);
  const totalWeight = selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">批量结算</h3>
            <p className="text-sm text-slate-500 mt-1">按供应商分组显示未发起结算的入库单</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedBySupplier).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package size={48} className="mx-auto text-slate-300 mb-4" />
              <p>暂无未发起结算的入库单</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedBySupplier).map(([supplier, docs]) => {
                const supplierSelectedIds = docs.filter(d => selectedIds.includes(d.id)).map(d => d.id);
                const isAllSelected = supplierSelectedIds.length === docs.length;
                const supplierTotal = docs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0);
                const supplierWeight = docs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

                return (
                  <div key={supplier} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* 供应商标题栏 */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            docs.forEach(doc => {
                              onToggleSelect(doc.id, !isAllSelected);
                            });
                          }}
                          className={`p-1 rounded ${isAllSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          {isAllSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                        <span className="font-medium text-slate-800">{supplier}</span>
                        <span className="text-sm text-slate-500">{docs.length}条入库单</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">总重量: </span>
                        <span className="font-medium text-slate-700">{supplierWeight.toFixed(2)}吨</span>
                        <span className="text-slate-400 mx-2">|</span>
                        <span className="text-slate-500">总金额: </span>
                        <span className="font-medium text-orange-600">¥{supplierTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* 入库单列表 */}
                    <div className="divide-y divide-slate-100">
                      {docs.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            onClick={() => onToggleSelect(doc.id, !isSelected)}
                            className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleSelect(doc.id, !isSelected);
                                }}
                                className={`p-1 rounded ${isSelected ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                              </button>
                              <div>
                                <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                                <span className="mx-2 text-slate-300">|</span>
                                <span className="text-sm text-slate-600">{doc.materialName}</span>
                              </div>
                            </div>
                            <div className="text-sm">
                              <span className="text-slate-500">{doc.actualWeight}吨</span>
                              <span className="text-slate-400 mx-2">×</span>
                              <span className="text-slate-500">¥{doc.price}/吨</span>
                              <span className="text-slate-400 mx-2">=</span>
                              <span className="font-medium text-orange-600">¥{doc.amountWithTax?.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              已选择 <span className="font-medium text-slate-800">{selectedDocs.length}</span> 条入库单，
              总重量 <span className="font-medium text-slate-800">{totalWeight.toFixed(2)}吨</span>，
              总金额 <span className="font-medium text-orange-600">¥{totalAmount.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={() => {
                  onConfirm(selectedDocs);
                  onClose();
                }}
                disabled={selectedDocs.length === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                确认生成结算单
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockinManageView = ({ deliveries = [], orders = [], onDetailClick, onOrderClick, onDeliveryClick, onGenerateDocument, initialFilter, setInitialFilter }) => {
  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  // 批量结算状态
  const [showBatchSettlementModal, setShowBatchSettlementModal] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState([]);

  // 处理从首页传递的初始筛选条件
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.status) {
        setFilterStatus(initialFilter.status);
      }
      setInitialFilter?.(null);
    }
  }, [initialFilter, setInitialFilter]);

  // 从 deliveries 提取入库单数据
  const stockins = useMemo(() => {
    const docs = [];
    deliveries.forEach(delivery => {
      if (delivery.storage) {
        // 入库单结算状态：有结算单则为"已发起"，否则为"未发起"
        const stockinStatus = delivery.settlement ? '已发起' : '未发起';

        // 计算含税金额和不含税金额
        const price = delivery.price || orders.find(o => o.id === delivery.orderId)?.price || 0;
        const taxRate = delivery.storage.taxRate || 1;  // 默认税率1%
        const amountWithoutTax = delivery.storage.actualWeight * price;
        const amountWithTax = amountWithoutTax * (1 + taxRate / 100);

        docs.push({
          id: delivery.storage.stockInNo,
          docNo: delivery.storage.stockInNo,
          docType: 'stockin',
          orderId: delivery.orderId,
          orderNo: delivery.orderNo,
          orderStatus: delivery.orderStatus,
          deliveryId: delivery.id,
          deliveryNo: delivery.deliveryNo,
          deliveryStatus: delivery.status,  // 送货单状态
          supplierId: delivery.supplierId,
          supplierName: delivery.supplierName,
          baseId: delivery.baseId,
          baseName: delivery.baseName,
          materialId: delivery.materialId,
          materialName: delivery.materialName,
          price: price,
          vehicleNo: delivery.reservation?.vehicleNo,
          grossWeight: delivery.storage.grossWeight,
          tareWeight: delivery.storage.tareWeight,
          receiveWeight: delivery.storage.actualWeight,  // 收货量
          deductionWeight: delivery.storage.deduction,  // 扣杂量
          actualWeight: delivery.storage.actualWeight,
          quality: delivery.storage.quality,
          stockInTime: delivery.storage.at,
          operator: delivery.storage.operator,
          status: stockinStatus,
          amountWithTax: delivery.storage.amountWithTax || amountWithTax,
          amountWithoutTax: delivery.storage.amountWithoutTax || amountWithoutTax,
          taxRate: taxRate,
          invoiceType: '反向开票',  // 默认反向开票
          createdAt: delivery.storage.at,
          // 环节信息（包含照片）
          entryInfo: delivery.storage.entryInfo,
          entryWeighInfo: delivery.storage.entryWeighInfo,
          qualityInfo: delivery.storage.qualityInfo,
          exitWeighInfo: delivery.storage.exitWeighInfo,
          exitCheckInfo: delivery.storage.exitCheckInfo,
          photos: delivery.storage.photos,
          delivery,
        });
      }
    });
    return docs;
  }, [deliveries, orders]);

  // 获取唯一的基地和供应商列表
  const { bases, suppliers } = useMemo(() => {
    const baseSet = new Set();
    const supplierSet = new Set();
    stockins.forEach(doc => {
      if (doc.baseName) baseSet.add(doc.baseName);
      if (doc.supplierName) supplierSet.add(doc.supplierName);
    });
    return {
      bases: Array.from(baseSet).sort(),
      suppliers: Array.from(supplierSet).sort()
    };
  }, [stockins]);

  // 筛选后的数据
  const filteredDocs = useMemo(() => {
    let result = [...stockins];

    // 搜索筛选
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc =>
        doc.docNo?.toLowerCase().includes(query) ||
        doc.orderNo?.toLowerCase().includes(query) ||
        doc.deliveryNo?.toLowerCase().includes(query) ||
        doc.supplierName?.toLowerCase().includes(query) ||
        doc.vehicleNo?.toLowerCase().includes(query)
      );
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(doc => doc.baseName === filterBase);
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(doc => doc.supplierName === filterSupplier);
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(doc => doc.status === filterStatus);
    }

    // 日期筛选
    if (filterDateStart) {
      result = result.filter(doc => {
        const docDate = doc.stockInTime?.split(' ')[0];
        return docDate && docDate >= filterDateStart;
      });
    }
    if (filterDateEnd) {
      result = result.filter(doc => {
        const docDate = doc.stockInTime?.split(' ')[0];
        return docDate && docDate <= filterDateEnd;
      });
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [stockins, searchQuery, filterBase, filterSupplier, filterStatus, filterDateStart, filterDateEnd]);

  // 获取未发起结算的入库单
  const unsettledStockins = useMemo(() => {
    return stockins.filter(doc => doc.status === '未发起');
  }, [stockins]);

  // 统计数据
  const stats = useMemo(() => {
    const total = stockins.length;
    const notInitiated = unsettledStockins.length;
    const initiated = stockins.filter(d => d.status === '已发起').length;
    const totalWeight = stockins.reduce((sum, d) => sum + (d.actualWeight || 0), 0);
    return { total, notInitiated, initiated, totalWeight };
  }, [stockins, unsettledStockins]);

  // 批量结算 - 切换选择
  const toggleBatchSelect = (docId, isSelected) => {
    setBatchSelectedIds(prev =>
      isSelected
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };

  // 打开批量结算弹窗
  const openBatchSettlement = () => {
    setBatchSelectedIds(unsettledStockins.map(doc => doc.id));
    setShowBatchSettlementModal(true);
  };

  // 确认批量生成结算单
  const confirmBatchSettlement = (selectedDocs) => {
    onGenerateDocument?.({
      sourceType: 'stockin',
      targetType: 'settlement',
      targetLabel: '结算单',
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0),
      totalWeight: selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
      isBatch: true,
    });
  };

  // 清除所有筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterBase('');
    setFilterSupplier('');
    setFilterStatus('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  const hasActiveFilters = searchQuery || filterBase || filterSupplier || filterStatus || filterDateStart || filterDateEnd;

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">入库单管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有入库单据</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">全部入库单</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">未发起结算</p>
              <p className="text-2xl font-bold text-slate-600 mt-1">{stats.notInitiated}</p>
            </div>
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-slate-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">已发起结算</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.initiated}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-green-500" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">入库总吨位</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.totalWeight.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Truck size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索单号、供应商、车牌号..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 基地筛选 */}
            <select
              value={filterBase}
              onChange={(e) => setFilterBase(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部基地</option>
              {bases.map(base => (
                <option key={base} value={base}>{base}</option>
              ))}
            </select>

            {/* 供应商筛选 */}
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部供应商</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>

            {/* 状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">全部状态</option>
              <option value="未发起">未发起</option>
              <option value="已发起">已发起</option>
            </select>

            {/* 日期筛选 */}
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <span className="text-slate-400">至</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            {/* 批量结算按钮 */}
            {unsettledStockins.length > 0 && (
              <button
                onClick={openBatchSettlement}
                className="flex items-center gap-1 px-4 py-2.5 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
              >
                <CheckSquare size={16} />
                批量结算
                <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                  {unsettledStockins.length}
                </span>
              </button>
            )}

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 数据表格 */}
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full min-w-max">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">入库单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">订单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">送货单号</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">供应商</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">物料</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">车牌号</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">毛重</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">皮重</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">扣杂量</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">收货量(吨)</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">含税金额</th>
                <th className="text-right p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">不含税金额</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">税率</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">开票类型</th>
                <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">入库时间</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">结算状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">送货单状态</th>
                <th className="text-center p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan="18" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Package size={40} className="text-slate-300" />
                      <p>暂无入库单数据</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredDocs.map((doc) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td className="p-3">
                      <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOrderClick?.(doc.orderId);
                        }}
                        className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {doc.orderNo}
                      </button>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeliveryClick?.(doc.deliveryId);
                        }}
                        className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {doc.deliveryNo}
                      </button>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-800 font-medium text-sm">{doc.supplierName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-600 text-sm">{doc.materialName}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-600 font-mono text-sm">{doc.vehicleNo}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-slate-600 text-sm">{doc.grossWeight}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-slate-600 text-sm">{doc.tareWeight}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-slate-600 text-sm">{doc.deductionWeight}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-medium text-slate-800 text-sm">{doc.receiveWeight}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-bold text-orange-600 text-sm">¥{doc.amountWithTax?.toLocaleString()}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-slate-600 text-sm">¥{doc.amountWithoutTax?.toLocaleString()}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="text-slate-600 text-sm">{doc.taxRate}%</span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-600 text-sm">{doc.invoiceType}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-600 text-sm whitespace-nowrap">{doc.stockInTime}</span>
                    </td>
                    <td className="p-3 text-center">
                      <StatusBadge status={doc.status} type="stockin" />
                    </td>
                    <td className="p-3 text-center">
                      <DeliveryStatusBadge status={doc.delivery?.status} />
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDetailClick?.(doc);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Eye size={12} />
                        详情
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 底部统计 */}
        {filteredDocs.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredDocs.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{stockins.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 批量结算弹窗 */}
      <BatchSettlementModal
        isOpen={showBatchSettlementModal}
        onClose={() => setShowBatchSettlementModal(false)}
        onConfirm={confirmBatchSettlement}
        stockins={unsettledStockins}
        onToggleSelect={toggleBatchSelect}
        selectedIds={batchSelectedIds}
      />
    </div>
  );
};

export default StockinManageView;
